import { useEffect, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { Save, Upload, Check } from "lucide-react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSettings } from "@/context/SettingsContext";

const BACKGROUNDS = [
  { id: "confetti", label: "Confetti" },
  { id: "dots", label: "Dots" },
  { id: "stripes", label: "Sunny Stripes" },
  { id: "grid", label: "Grid" },
  { id: "sky-solid", label: "Sky" },
  { id: "pink-solid", label: "Pink" },
  { id: "cream-solid", label: "Cream" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const heroSrc = (v) => (v ? (v.startsWith("http") ? v : `${BACKEND}${v}`) : "");

function HeroSlot({ index, value, onChange }) {
  const onDrop = useCallback(async (files) => {
    const fd = new FormData();
    fd.append("file", files[0]);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`/api/files/${res.data.id}`);
      toast.success(`Showcase image ${index + 1} updated`);
    } catch { toast.error("Upload failed"); }
  }, [index, onChange]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [] }, maxFiles: 1 });
  return (
    <div {...getRootProps()} data-testid={`hero-slot-${index}`}
      className={`relative border-[3px] border-dashed border-ink rounded-xl overflow-hidden cursor-pointer h-40 flex items-center justify-center ${isDragActive ? "bg-sky/40" : "bg-cream-solid hover:bg-sun/30"}`}>
      <input {...getInputProps()} />
      {value ? (
        <>
          <img src={heroSrc(value)} alt={`showcase ${index + 1}`} className="w-full h-full object-cover" />
          <span className="absolute bottom-1 left-1 tag-pill bg-white text-[10px]">Replace #{index + 1}</span>
        </>
      ) : (
        <div className="text-center"><Upload className="w-6 h-6 mx-auto" /><span className="text-sm font-semibold block mt-1">Image {index + 1}</span></div>
      )}
    </div>
  );
}

function ArtistPhoto({ value, onChange }) {
  const onDrop = useCallback(async (files) => {
    const fd = new FormData();
    fd.append("file", files[0]);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`/api/files/${res.data.id}`);
      toast.success("Artist photo updated");
    } catch { toast.error("Upload failed"); }
  }, [onChange]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { "image/*": [] }, maxFiles: 1 });
  return (
    <div {...getRootProps()} data-testid="set-artist-photo"
      className={`relative border-[3px] border-dashed border-ink rounded-xl overflow-hidden cursor-pointer h-48 sm:w-48 flex items-center justify-center ${isDragActive ? "bg-sky/40" : "bg-cream-solid hover:bg-sun/30"}`}>
      <input {...getInputProps()} />
      {value ? (
        <>
          <img src={heroSrc(value)} alt="artist" className="w-full h-full object-cover" />
          <span className="absolute bottom-1 left-1 tag-pill bg-white text-[10px]">Replace photo</span>
        </>
      ) : (
        <div className="text-center"><Upload className="w-6 h-6 mx-auto" /><span className="text-sm font-semibold block mt-1">Upload photo</span></div>
      )}
    </div>
  );
}

function QrUpload({ label, value, onChange, testid }) {
  const onDrop = useCallback(async (files) => {
    const fd = new FormData();
    fd.append("file", files[0]);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.id);
      toast.success(`${label} QR uploaded`);
    } catch { toast.error("Upload failed"); }
  }, [label, onChange]);
  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { "image/*": [] }, maxFiles: 1 });
  return (
    <div>
      <label className="font-display font-semibold text-sm">{label} QR code</label>
      <div {...getRootProps()} data-testid={testid} className="mt-1 border-[3px] border-dashed border-ink rounded-xl p-4 text-center cursor-pointer bg-cream-solid hover:bg-sun/30 flex items-center gap-3 justify-center">
        <input {...getInputProps()} />
        {value ? <img src={`${API}/files/${value}`} alt="qr" className="w-16 h-16 object-contain" /> : <Upload className="w-6 h-6" />}
        <span className="text-sm font-semibold">{value ? "Replace QR" : "Upload QR image"}</span>
      </div>
    </div>
  );
}

export default function SettingsTab() {
  const { settings: global, reloadSettings } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="font-display text-xl">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); await reloadSettings(); toast.success("Saved! Your site is updated. ✨"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <h1 className="font-display font-bold text-3xl mb-1">Settings</h1>
      <p className="text-ink/60 mb-6">Customize your shop, payments & look.</p>

      <div className="space-y-6">
        <section className="brut-card p-6 space-y-4">
          <h2 className="font-display font-bold text-xl">Shop</h2>
          <div><label className="font-display font-semibold text-sm">Site title (shop name)</label><Input value={s.shop_name} onChange={(e) => set("shop_name", e.target.value)} className="brut-input mt-1" data-testid="set-shop-name" /></div>
          <div><label className="font-display font-semibold text-sm">Tagline</label><Textarea value={s.tagline} onChange={(e) => set("tagline", e.target.value)} className="brut-input mt-1" data-testid="set-tagline" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="font-display font-semibold text-sm">Instagram URL</label><Input value={s.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} className="brut-input mt-1" data-testid="set-instagram" /></div>
            <div><label className="font-display font-semibold text-sm">Contact email</label><Input value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="brut-input mt-1" data-testid="set-email" /></div>
          </div>
        </section>

        <section className="brut-card p-6 space-y-4">
          <h2 className="font-display font-bold text-xl">About the artist</h2>
          <p className="text-ink/60 text-sm">Shown in the "the human behind the pen" section on your home page.</p>
          <div className="flex flex-col sm:flex-row gap-5">
            <ArtistPhoto value={s.artist_image} onChange={(v) => set("artist_image", v)} />
            <div className="flex-1 space-y-4">
              <div><label className="font-display font-semibold text-sm">Heading</label><Input value={s.artist_heading || ""} onChange={(e) => set("artist_heading", e.target.value)} className="brut-input mt-1" data-testid="set-artist-heading" /></div>
              <div><label className="font-display font-semibold text-sm">Bio</label><Textarea value={s.artist_bio || ""} onChange={(e) => set("artist_bio", e.target.value)} className="brut-input mt-1 min-h-28" data-testid="set-artist-bio" /></div>
            </div>
          </div>
        </section>

        <section className="brut-card p-6 space-y-4">
          <h2 className="font-display font-bold text-xl">Payments — Venmo & Cash App</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="font-display font-semibold text-sm">Venmo name</label><Input value={s.venmo_name} onChange={(e) => set("venmo_name", e.target.value)} className="brut-input mt-1" placeholder="@YourVenmo" data-testid="set-venmo-name" /></div>
            <div><label className="font-display font-semibold text-sm">Cash App name</label><Input value={s.cashapp_name} onChange={(e) => set("cashapp_name", e.target.value)} className="brut-input mt-1" placeholder="$YourCashApp" data-testid="set-cashapp-name" /></div>
            <QrUpload label="Venmo" value={s.venmo_qr} onChange={(v) => set("venmo_qr", v)} testid="set-venmo-qr" />
            <QrUpload label="Cash App" value={s.cashapp_qr} onChange={(v) => set("cashapp_qr", v)} testid="set-cashapp-qr" />
          </div>
        </section>

        <section className="brut-card p-6 space-y-4">
          <h2 className="font-display font-bold text-xl">Discounts</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="font-display font-semibold text-sm">Promo signup discount (%)</label><Input type="number" value={s.promo_discount} onChange={(e) => set("promo_discount", Number(e.target.value))} className="brut-input mt-1" data-testid="set-promo-discount" /></div>
            <div><label className="font-display font-semibold text-sm">Referral discount (%)</label><Input type="number" value={s.referral_discount} onChange={(e) => set("referral_discount", Number(e.target.value))} className="brut-input mt-1" data-testid="set-referral-discount" /></div>
          </div>
        </section>

        <section className="brut-card p-6">
          <h2 className="font-display font-bold text-xl mb-4">Page background</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {BACKGROUNDS.map((b) => (
              <button key={b.id} onClick={() => set("background", b.id)} data-testid={`bg-${b.id}`}
                className={`relative h-20 rounded-xl border-[3px] border-ink bg-${b.id} overflow-hidden transition-transform hover:-translate-y-0.5 ${s.background === b.id ? "shadow-brutlg ring-4 ring-sky" : "shadow-brut"}`}>
                <span className="absolute bottom-1 left-1 tag-pill bg-white text-[10px]">{b.label}</span>
                {s.background === b.id && <span className="absolute top-1 right-1 bg-ink text-cream rounded-full p-1"><Check className="w-3 h-3" /></span>}
              </button>
            ))}
          </div>
        </section>

        <section className="brut-card p-6">
          <h2 className="font-display font-bold text-xl mb-1">Front-page showcase</h2>
          <p className="text-ink/60 text-sm mb-4">The 3 caricatures shown in the hero on your home page. Click a slot to swap it.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <HeroSlot key={i} index={i} value={(s.hero_images || [])[i] || ""}
                onChange={(url) => {
                  const arr = [...(s.hero_images || ["", "", ""])];
                  while (arr.length < 3) arr.push("");
                  arr[i] = url;
                  set("hero_images", arr);
                }} />
            ))}
          </div>
        </section>

        <button onClick={save} disabled={saving} className="brut-btn bg-bubble text-white text-lg" data-testid="save-settings">
          <Save className="w-5 h-5" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
